import { useCallback, useEffect, useRef, useState } from 'react';
import { useClient } from '../context/ClientContext';
import { useApiClient } from '../api/apiClient';
import { createAssetFindingsLoader } from '../utils/assetFindingsLoader';

const empty = key => ({ key, findings: [], links: [], total: null, page: 0, loading: true, error: null,
  mode: 'asset', sourceTotal: null, checked: 0, complete: false, hasMore: false, coverageWarning: null });

// Asset history must not depend on the tenant-wide first page or its cache.
export function useAssetFindings(assetId) {
  const { clientId } = useClient();
  const api = useApiClient();
  const key = clientId && assetId ? JSON.stringify([clientId, assetId]) : '';
  const [state, setState] = useState(() => empty(key));
  const generation = useRef(0);
  const busy = useRef(false);
  const session = useRef(null);

  const fetchPage = useCallback(async (page, token) => {
    if (!key || busy.current || generation.current !== token) return;
    busy.current = true;
    setState(previous => ({ ...(previous.key === key ? previous : empty(key)), loading: true, error: null }));
    try {
      const result = await session.current.load(page);
      if (generation.current === token) setState({ key, ...result });
    } catch (error) {
      if (generation.current === token) setState(previous => ({ ...previous, loading: false, error, complete: false }));
    } finally {
      if (generation.current === token) busy.current = false;
    }
  }, [key]);

  const reset = useCallback(() => {
    const token = ++generation.current;
    busy.current = false;
    session.current = createAssetFindingsLoader(api, clientId, assetId, () => generation.current === token,
      progress => { if (generation.current === token) setState({ key, ...progress }); });
    setState(empty(key));
    if (key) void fetchPage(1, token);
  }, [api, clientId, assetId, key, fetchPage]);

  useEffect(() => {
    reset();
    return () => { generation.current++; busy.current = false; };
  }, [reset]);

  const visible = state.key === key ? state : empty(key);
  const loadMore = useCallback(() => {
    if (!visible.loading && !visible.error && visible.hasMore) {
      void fetchPage(visible.page + 1, generation.current);
    }
  }, [visible, fetchPage]);
  return { ...visible, loading: Boolean(key) && visible.loading, loadMore, refetch: reset };
}
